import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useField } from 'formik'
import SelectField from 'components/molecules/select-field'
import ProductAttributesApiV2 from 'api/v2/admins/product-attributes-api-v2'
import { limitFilter } from 'utils/generator-utils'

const propTypes = {
  code       : PropTypes.string,
  name       : PropTypes.string,
  label      : PropTypes.string,
  required   : PropTypes.bool,
  isMulti    : PropTypes.bool,
  isCuration : PropTypes.bool
}

const defaultProps = {
  isCuration : false,
  isMulti    : false
}

const DEFAULT_OPTION_LENGTH = 20

const ProductAttributeSelectField = ({
  code,
  name,
  label,
  required,
  isMulti,
  isCuration,
  ...props
}) => {
  const [ initialOption, setInitialOption ]   = useState()
  const [ defaultOptions, setDefaultOptions ] = useState([])
  const [ field ]                             = useField(name)
  const { value }                             = field

  /**
   * Fetch product attribut. Get the options if it's present on the session storage.
   * Otherwise, making a call to the endpoint
   * @return [array] list of attribute
   */
  const fetchAttribute = useCallback(async () => {
    let options = []

    try {
      const optionFromCache = sessionStorage.getItem(code)

      // return JSON parsed options if it's exist on the cache
      if (optionFromCache) options = JSON.parse(optionFromCache)
      else {
        const optionEndpoint = isCuration
          ? ProductAttributesApiV2.getCurationOptions
          : ProductAttributesApiV2.getOptions

        const response = await optionEndpoint({ attributeCode: code })
        options        = response.data.options

        sessionStorage.setItem(code, JSON.stringify(options))
      }
    }
    catch (error) {
      alert(`Failed: Get Attribute ${code}`)
    }

    const displayedOptions = options.slice(0, DEFAULT_OPTION_LENGTH)
    setDefaultOptions(displayedOptions.map((option) => formatOption(option)))

    return options
  }, [ code, isCuration ])

  /** set initial option by find the particular option if the value is on the options */
  const setDefaultOption = useCallback(async () => {
    const options = await fetchAttribute()

    // there' some case that value is still undefined
    // for now, we can solve by skip it before it go through to the logic below
    if (!value) return

    if (isMulti) {
      const selectedOptions = options.filter((option) => value.includes(option.id))

      if (selectedOptions.length === 0) return
      setInitialOption(selectedOptions.map((option) => formatOption(option)))
    }
    else {
      const selectedOption = options.find((option) => option.id === Number(value))

      if (!selectedOption) return
      setInitialOption(formatOption(selectedOption))
    }
  }, [ isMulti, value, fetchAttribute ])

  useEffect(() => {
    fetchAttribute()
  }, [ fetchAttribute ])

  useEffect(() => {
    /** expecting it always render once at the first time */
    setDefaultOption()
  }, [ setDefaultOption ])

  /**
   * Conditional filter to compare exist value on option with input value on field
   * @param {object} option -  object consist of attribute 'called' label for dropdown option
   * @param {string} inputValue - input value from the field
   * @returns {boolean} true if start input match with the option
   */
  const conditionalFilterOptions = (option, inputValue) => {
    const noSpaceInput  = inputValue.replaceAll(' ','')
    const noSpaceOption = option.label.replaceAll(' ', '')

    const inputLowerCase  = noSpaceInput.toLocaleLowerCase()
    const optionLowerCase = noSpaceOption.toLocaleLowerCase()

    return optionLowerCase.includes(inputLowerCase)
  }

  /**
   * Custom handler filter options that triggered when user type.
   * @param {string} inputValue - Input value from the field
   * @returns {array} list of options that already transform into react-select standart option
   */
  const onFilterOptions = async (inputValue) => {
    const jsonOptions = await fetchAttribute()

    const filteredOptions = Array.from(limitFilter(jsonOptions, (option) => conditionalFilterOptions(option, inputValue), DEFAULT_OPTION_LENGTH))
    return filteredOptions.map((option) => formatOption(option))
  }

  /** Format the option structure to follow react-select convention */
  const formatOption = (option) => ({
    value : option.id,
    label : option.label
  })

  return (
    <SelectField
      { ...props }
      name={ name }
      label={ label }
      isMulti={ isMulti }
      required={ required }
      defaultValue={ initialOption }
      defaultOptions={ defaultOptions }
      onFilterOptions={ onFilterOptions }
    />
  )
}

ProductAttributeSelectField.propTypes    = propTypes
ProductAttributeSelectField.defaultProps = defaultProps

export default ProductAttributeSelectField
